if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        this.currentProduct = null;
        this.selectedVariant = null;
        this.quantity = 1;

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        // Get the product handle from the opener
        const productUrl = opener.getAttribute('data-product-url');
        const productHandle = this.extractProductHandle(productUrl);

        if (productHandle) {
          // Use the upsells modal system instead of the current modal
          this.openVariantModal(productHandle);

          // Remove loading state
          opener.removeAttribute('aria-disabled');
          opener.classList.remove('loading');
          opener.querySelector('.loading__spinner').classList.add('hidden');
        } else {
          // Fallback to original method if no handle found
          fetch(productUrl)
            .then((response) => response.text())
            .then((responseText) => {
              const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
              const productElement = responseHTML.querySelector('product-info');

              this.preprocessHTML(productElement);
              this.modalContent.innerHTML = productElement.outerHTML;

              if (window.Shopify && Shopify.PaymentButton) {
                Shopify.PaymentButton.init();
              }
              if (window.ProductModel) window.ProductModel.loadShopifyXR();

              super.show(opener);
            })
            .finally(() => {
              opener.removeAttribute('aria-disabled');
              opener.classList.remove('loading');
              opener.querySelector('.loading__spinner').classList.add('hidden');
            });
        }
      }

      extractProductHandle(productUrl) {
        // Extract product handle from URL like /products/product-handle
        const match = productUrl.match(/\/products\/([^\/\?]+)/);
        return match ? match[1] : null;
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }

      // Upsells modal methods
      async openVariantModal(productHandle) {
        try {
          console.log("Opening modal for product handle:", productHandle);

          // Remove any existing modal
          const existingModal = document.getElementById("variant-modal");
          if (existingModal) {
            existingModal.remove();
          }

          // Create the modal element
          const modalElement = document.createElement("div");
          modalElement.id = "variant-modal";
          modalElement.className = "variant-modal";
          modalElement.style.display = "none";
          modalElement.innerHTML = `
            <div class="variant-modal-content">
              <span class="close-modal">&times;</span>
              <div class="variant-modal-layout">
                <div class="variant-modal-image">
                  <img id="variant-image" src="" alt="Product image">
                </div>
                <div class="variant-modal-details">
                  <h3 id="modal-product-title"></h3>
                  <div class="modal-product-price-container">
                    <span id="modal-product-price" class="modal-product-price"></span>
                    <span id="modal-product-price-excluding-vat" class="excluding-vat-price" style="font-size: 12px"></span>
                  </div>
                  <div id="variant-options"></div>

                  <div class="quantity-popover-container">
                    <quantity-input class="quantity cart-quantity">
                      <button class="quantity__button" name="minus" type="button">
                        <span class="visually-hidden">Decrease quantity</span>
                        <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-minus" fill="none" viewBox="0 0 10 2">
                          <path fill-rule="evenodd" clip-rule="evenodd" d="M.5 1C.5.7.7.5 1 .5h8a.5.5 0 110 1H1A.5.5 0 01.5 1z" fill="currentColor"></path>
                        </svg>
                      </button>
                      <input class="quantity__input" type="number" name="quantity" value="1" min="1" step="1" aria-label="Quantity" id="modal-quantity-input">
                      <button class="quantity__button" name="plus" type="button">
                        <span class="visually-hidden">Increase quantity</span>
                        <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-plus" fill="none" viewBox="0 0 10 10">
                          <path fill-rule="evenodd" clip-rule="evenodd" d="M1 4.51a.5.5 0 000 1h3.5l.01 3.5a.5.5 0 001-.01V5.5l3.5-.01a.5.5 0 00-.01-1H5.5L5.49.99a.5.5 0 00-1 .01v3.5l-3.5.01H1z" fill="currentColor"></path>
                        </svg>
                      </button>
                    </quantity-input>
                  </div>

                  <button id="add-selected-variant" class="button">Add to Cart</button>
                </div>
              </div>
            </div>
          `;

          // Get the VAT amount from the data attribute
          const vatAmountElement = document.querySelector(".vat-amount");
          const vatAmount = vatAmountElement
            ? parseFloat(vatAmountElement.dataset.vatAmount)
            : 21; // Default to 21% if not found

          // Store VAT amount for later use
          this.vatAmount = vatAmount;

          // Append the modal to the body
          document.body.appendChild(modalElement);

          // Fetch product data using the handle
          const response = await fetch(`/products/${productHandle}.js`);

          if (!response.ok) {
            throw new Error(`Failed to fetch product data: ${response.status}`);
          }

          const product = await response.json();
          console.log("Product data:", product);

          // Set product title in modal
          const modalTitle = document.querySelector("#modal-product-title");
          if (modalTitle) {
            modalTitle.textContent = product.title;
          }

          // Set initial product image
          const variantImage = document.querySelector("#variant-image");
          if (variantImage && product.featured_image) {
            variantImage.src = product.featured_image;
            variantImage.alt = product.title;
          }

          // Generate variant options
          const variantOptionsContainer =
            document.querySelector("#variant-options");
          if (variantOptionsContainer) {
            variantOptionsContainer.innerHTML = "";

            // Store the product data for later use
            this.currentProduct = product;

            // Create option selectors for each option type
            product.options.forEach((option, index) => {
              const optionContainer = document.createElement("div");
              optionContainer.className = "variant-option product-form__input";

              const label = document.createElement("label");
              label.className = "variant-option-title form__label";
              label.textContent = option.name;
              optionContainer.appendChild(label);

              const radioGroup = document.createElement("div");
              radioGroup.className = "variant-radio-group";

              // Add radio buttons for each option value
              option.values.forEach((value) => {
                const radioContainer = document.createElement("div");
                radioContainer.className = "variant-radio-container";

                const radioInput = document.createElement("input");
                radioInput.type = "radio";
                radioInput.id = `option-${index}-${value
                  .replace(/\s+/g, "-")
                  .toLowerCase()}`;
                radioInput.name = `option-${index}`;
                radioInput.value = value;
                radioInput.className = "variant-radio-input";
                radioInput.dataset.optionIndex = index;

                // Select the first option by default
                if (option.values.indexOf(value) === 0) {
                  radioInput.checked = true;
                }

                const radioLabel = document.createElement("label");
                radioLabel.htmlFor = radioInput.id;
                radioLabel.className = "variant-radio-label";
                radioLabel.textContent = value;

                // Add change event to update available variants
                radioInput.addEventListener("change", () => {
                  this.updateAvailableVariants();
                });

                radioContainer.appendChild(radioInput);
                radioContainer.appendChild(radioLabel);
                radioGroup.appendChild(radioContainer);
              });

              optionContainer.appendChild(radioGroup);
              variantOptionsContainer.appendChild(optionContainer);
            });

            // Initialize available variants
            this.updateAvailableVariants();
          }

          // Set up quantity selector functionality
          this.setupQuantitySelector();

          // Add event listener for modal close button
          const closeModal = document.querySelector(".close-modal");
          if (closeModal) {
            closeModal.addEventListener("click", () => {
              console.log("Closing modal");
              this.closeVariantModal();
            });
          }

          // Add event listener for add selected variant button
          const addSelectedVariant = document.querySelector(
            "#add-selected-variant"
          );
          if (addSelectedVariant) {
            addSelectedVariant.addEventListener("click", () => {
              console.log("Adding selected variant to cart");
              this.addSelectedVariantToCart();
            });
          }

          // Show modal
          modalElement.style.display = "flex";
        } catch (error) {
          console.error("Error fetching product data:", error);
        }
      }

      setupQuantitySelector() {
        const quantityInput = document.querySelector("#modal-quantity-input");
        const minusButton = document.querySelector('button[name="minus"]');
        const plusButton = document.querySelector('button[name="plus"]');

        if (quantityInput && minusButton && plusButton) {
          // Set initial quantity
          this.quantity = 1;

          // Minus button functionality
          minusButton.addEventListener("click", () => {
            const currentValue = parseInt(quantityInput.value, 10);
            if (currentValue > 1) {
              quantityInput.value = currentValue - 1;
              this.quantity = currentValue - 1;
            }
          });

          // Plus button functionality
          plusButton.addEventListener("click", () => {
            const currentValue = parseInt(quantityInput.value, 10);
            quantityInput.value = currentValue + 1;
            this.quantity = currentValue + 1;
          });

          // Input change functionality
          quantityInput.addEventListener("change", () => {
            let value = parseInt(quantityInput.value, 10);

            // Ensure minimum value of 1
            if (isNaN(value) || value < 1) {
              value = 1;
              quantityInput.value = 1;
            }

            this.quantity = value;
          });
        }
      }

      closeVariantModal() {
        const modal = document.getElementById("variant-modal");
        if (modal) {
          modal.style.display = "none";
          // Optionally remove the modal from DOM completely
          modal.remove();
        }
      }

      updateAvailableVariants() {
        if (!this.currentProduct) return;

        // Get all option radio groups
        const optionGroups = [];
        for (let i = 0; i < this.currentProduct.options.length; i++) {
          optionGroups.push(
            document.querySelectorAll(`input[name="option-${i}"]:checked`)
          );
        }

        // Get selected options
        const selectedOptions = optionGroups
          .map((group) => {
            if (group.length > 0) {
              return group[0].value;
            }
            return null;
          })
          .filter(Boolean);

        // Find matching variant
        this.selectedVariant = this.currentProduct.variants.find((variant) => {
          return variant.options.every(
            (option, index) => option === selectedOptions[index]
          );
        });

        // Update add button state
        const addButton = document.querySelector("#add-selected-variant");
        if (addButton) {
          if (this.selectedVariant && this.selectedVariant.available) {
            addButton.disabled = false;
            addButton.textContent = "Add to Cart";
          } else {
            addButton.disabled = true;
            addButton.textContent = "Out of Stock";
          }
        }

        // Update price display
        this.updatePriceDisplay();

        // Update the variant image
        this.updateVariantImage();
      }

      updatePriceDisplay() {
        if (!this.selectedVariant) return;

        const priceElement = document.querySelector("#modal-product-price");
        const priceExcludingVatElement = document.querySelector(
          "#modal-product-price-excluding-vat"
        );

        if (priceElement && this.selectedVariant.price) {
          // Format the price with the currency
          const formattedPrice = this.formatMoney(this.selectedVariant.price);
          priceElement.textContent = formattedPrice;

          // Calculate and display the price excluding VAT
          if (priceExcludingVatElement && this.vatAmount) {
            const vatDivider = (100 + this.vatAmount) / 100.0;
            const priceExcludingVat = Math.floor(
              this.selectedVariant.price / vatDivider
            );
            const formattedExVatPrice = this.formatMoney(priceExcludingVat);

            // Get the taxes excluded label from a data attribute or use a default
            const taxesExcludedLabel =
              document.querySelector("[data-taxes-excluded-label]")?.dataset
                .taxesExcludedLabel || "Excl. VAT";
            priceExcludingVatElement.textContent = `${formattedExVatPrice} ${taxesExcludedLabel}`;
          }
        }
      }

      formatMoney(cents) {
        // Use Shopify's formatMoney if available, otherwise use a simple formatter
        if (window.Shopify && window.Shopify.formatMoney) {
          return window.Shopify.formatMoney(cents);
        }

        // Simple money formatter as fallback
        const amount = cents / 100;
        const formatter = new Intl.NumberFormat(
          document.documentElement.lang || "en-US",
          {
            style: "currency",
            currency:
              document.querySelector('meta[property="og:price:currency"]')
                ?.content || "EUR",
          }
        );

        return formatter.format(amount);
      }

      updateVariantImage() {
        if (!this.currentProduct || !this.selectedVariant) return;

        const variantImage = document.querySelector("#variant-image");
        if (!variantImage) return;

        // Find the image for the selected variant
        let imageUrl = "";

        // First check if the variant has a specific image
        if (this.selectedVariant.featured_image) {
          imageUrl = this.selectedVariant.featured_image.src;
        } else if (this.selectedVariant.image_id) {
          // If variant has an image_id, find the matching image
          const matchingImage = this.currentProduct.images.find((image) => {
            // Some Shopify stores return image objects, others return URLs
            if (typeof image === "object") {
              return image.id === this.selectedVariant.image_id;
            } else {
              // If it's a URL, check if it contains the image_id
              return image.includes(this.selectedVariant.image_id);
            }
          });

          if (matchingImage) {
            imageUrl =
              typeof matchingImage === "object" ? matchingImage.src : matchingImage;
          }
        }

        // If no variant-specific image was found, use the product's featured image
        if (!imageUrl && this.currentProduct.featured_image) {
          imageUrl =
            typeof this.currentProduct.featured_image === "object"
              ? this.currentProduct.featured_image.src
              : this.currentProduct.featured_image;
        }

        // Update the image source
        if (imageUrl) {
          console.log("Updating variant image to:", imageUrl);
          variantImage.src = imageUrl;
          variantImage.alt = this.currentProduct.title;
        }
      }

      async addSelectedVariantToCart() {
        if (!this.selectedVariant) return;

        try {
          const quantity = this.quantity || 1;

          const response = await fetch("/cart/add.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              items: [
                {
                  id: this.selectedVariant.id,
                  quantity: quantity,
                },
              ],
            }),
          });

          if (!response.ok) throw new Error("Network response was not ok");

          // Get the updated cart data
          const cartData = await response.json();

          // Close modal
          this.closeVariantModal();

          // Update the cart drawer items
          await this.updateCartDrawer();

          // Update cart count bubble
          this.updateCartCountBubble(cartData);

          // Show the cart drawer/minicart
          this.showCartDrawer();

          // Dispatch cart:updated event to refresh cart items
          document.dispatchEvent(new CustomEvent("cart:updated"));
        } catch (error) {
          console.error("Error adding to cart:", error);
        }
      }

      updateCartCountBubble(cartData) {
        // Update cart count bubble
        const cartCountBubbles = document.querySelectorAll('.cart-count-bubble, [data-cart-count]');
        const itemCount = cartData.item_count || 0;

        cartCountBubbles.forEach(bubble => {
          if (bubble.classList.contains('cart-count-bubble')) {
            bubble.textContent = itemCount;
            bubble.style.display = itemCount > 0 ? 'block' : 'none';
          } else if (bubble.dataset.cartCount) {
            bubble.textContent = itemCount;
            bubble.style.display = itemCount > 0 ? 'block' : 'none';
          }
        });

        // Also update any elements with cart-count class
        const cartCountElements = document.querySelectorAll('.cart-count');
        cartCountElements.forEach(element => {
          element.textContent = itemCount;
        });
      }

      updateCartCountManually(cartData) {
        // Fallback method for manual cart count updates
        const cartCountBubbles = document.querySelectorAll('.cart-count-bubble, [data-cart-count]');
        const itemCount = cartData.item_count || 0;

        cartCountBubbles.forEach(bubble => {
          if (bubble.classList.contains('cart-count-bubble')) {
            bubble.textContent = itemCount;
            bubble.style.display = itemCount > 0 ? 'block' : 'none';
          } else if (bubble.dataset.cartCount) {
            bubble.textContent = itemCount;
            bubble.style.display = itemCount > 0 ? 'block' : 'none';
          }
        });

        // Also update any elements with cart-count class
        const cartCountElements = document.querySelectorAll('.cart-count');
        cartCountElements.forEach(element => {
          element.textContent = itemCount;
        });
      }

      showCartDrawer() {
        // Find and show the cart drawer
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        } else {
          // Fallback: try to trigger the cart icon bubble click event
          const cartIconBubble = document.querySelector('#cart-icon-bubble');
          if (cartIconBubble) {
            cartIconBubble.click();
          } else {
            // Last resort: look for cart drawer by ID or class
            const drawerElement = document.getElementById('cart-drawer') ||
                                 document.querySelector('.cart-drawer') ||
                                 document.querySelector('[data-cart-drawer]');

            if (drawerElement) {
              // Try to trigger the cart drawer to open
              const openEvent = new CustomEvent('cart:open');
              document.dispatchEvent(openEvent);

              // If the drawer has a show method, call it
              if (typeof drawerElement.show === 'function') {
                drawerElement.show();
              }

              // Add visible class or show the drawer
              drawerElement.classList.add('active', 'is-open');
              drawerElement.style.display = 'block';
              drawerElement.style.visibility = 'visible';
              drawerElement.style.opacity = '1';
            }
          }
        }
      }

      async updateCartDrawer() {
        try {
          // Fetch the cart page to get the updated HTML
          const response = await fetch("/cart?view=drawer");
          const html = await response.text();

          // Create a temporary element to parse the HTML
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, "text/html");

          // Find the cart-drawer-items element in the fetched HTML
          const newCartItems = doc.querySelector("cart-drawer-items");

          if (newCartItems) {
            // Find the cart-drawer-items element in the current page
            const currentCartItems = document.querySelector("cart-drawer-items");

            if (currentCartItems) {
              // Replace the current cart items with the new ones
              currentCartItems.innerHTML = newCartItems.innerHTML;
            }
          }

          // Check if we're on the cart page and update cart-items if needed
          if (window.location.pathname === "/cart") {
            const cartPageResponse = await fetch("/cart");
            const cartPageHtml = await cartPageResponse.text();

            const cartPageDoc = parser.parseFromString(cartPageHtml, "text/html");
            const newCartPageItems = cartPageDoc.querySelector("cart-items");

            if (newCartPageItems) {
              const currentCartPageItems = document.querySelector("cart-items");
              if (currentCartPageItems) {
                currentCartPageItems.innerHTML = newCartPageItems.innerHTML;
              }
            }
          }
        } catch (error) {
          console.error("Error updating cart drawer:", error);
        }
      }
    }
  );
}

function updateGlobalCartCountBubble(cartData) {
  const cartCountBubbles = document.querySelectorAll(
    ".cart-count-bubble, [data-cart-count]",
  );
  const itemCount = cartData.item_count || 0;
  console.log("Updating global cart count bubble to:", itemCount);

  cartCountBubbles.forEach((bubble) => {
    bubble.textContent = itemCount;
    bubble.style.display = itemCount > 0 ? "block" : "none";
  });

  const cartCountElements = document.querySelectorAll(".cart-count");
  cartCountElements.forEach((element) => {
    element.textContent = itemCount;
  });
}

async function syncGlobalCartCountBubble() {
  try {
    const response = await fetch("/cart.js");
    if (!response.ok) return;
    const cartData = await response.json();
    console.log(cartData);
    updateGlobalCartCountBubble(cartData);
  } catch (error) {
    console.error("Error syncing cart count:", error);
  }
}

window.addEventListener("cart-notification:open", syncGlobalCartCountBubble);
